import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
  ComposedChart, Line
} from 'recharts';
import {
  Monitor, Wrench, Activity, AlertTriangle, Download, TrendingUp, TrendingDown,
  Minus, ExternalLink, Clock, FileText, Lock, Unlock, RotateCcw, GripVertical, X
} from 'lucide-react';
import { api } from '../lib/api';
import { exportToPDF } from '../lib/pdfExport';
import StatCard from '../components/StatCard';
import ProductDrilldown from '../components/ProductDrilldown';
import CategoryDrilldown from '../components/CategoryDrilldown';
import clsx from 'clsx';

const LAYOUT_STORAGE_KEY = 'edframe-dashboard-layout';

// ============================================
// DRAGGABLE WIDGET COMPONENT
// ============================================

function DraggableWidget({
  id,
  title,
  children,
  isLocked,
  isDragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  className = '',
  fullWidth = false
}) {
  return (
    <div
      draggable={!isLocked}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', id);
        onDragStart(id);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(e, id);
      }}
      onDragEnd={onDragEnd}
      className={clsx(
        'card p-6 relative group transition-all',
        !isLocked && 'cursor-move',
        isDragging && 'opacity-50 scale-[0.98]',
        !isLocked && !isDragging && 'hover:ring-2 hover:ring-brand-500/30',
        fullWidth ? 'col-span-full' : '',
        className
      )}
    >
      {/* Drag Handle */}
      {!isLocked && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 bg-surface-200 dark:bg-surface-700/80 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span className="text-xs text-slate-500 dark:text-slate-400">Drag</span>
        </div>
      )}

      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      {children}
    </div>
  );
}

// ============================================
// LOADING SKELETON COMPONENTS
// ============================================

function SkeletonCard() {
  return (
    <div className="card p-6 animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="h-4 bg-surface-100 dark:bg-surface-700 rounded w-24" />
        <div className="w-10 h-10 bg-surface-100 dark:bg-surface-700 rounded-lg" />
      </div>
      <div className="h-8 bg-surface-100 dark:bg-surface-700 rounded w-20 mb-2" />
      <div className="h-3 bg-surface-100 dark:bg-surface-700 rounded w-16" />
    </div>
  );
}

function SkeletonChart({ height = 'h-64' }) {
  return (
    <div className={`card p-6 animate-pulse`}>
      <div className="h-5 bg-surface-100 dark:bg-surface-700 rounded w-40 mb-4" />
      <div className={`${height} bg-surface-100 dark:bg-surface-700/50 rounded`} />
    </div>
  );
}

// ============================================
// EMPTY STATE COMPONENT
// ============================================

function EmptyState({ title, description, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {Icon && <Icon className="w-12 h-12 text-slate-600 mb-4" />}
      <h3 className="text-lg font-medium text-slate-400 mb-2">{title}</h3>
      <p className="text-sm text-slate-500 max-w-md">{description}</p>
    </div>
  );
}

// ============================================
// FILTER PILL COMPONENT
// ============================================

function FilterPill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
        active
          ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/25'
          : 'bg-surface-100 dark:bg-surface-700 text-slate-600 dark:text-slate-400 hover:bg-surface-200 dark:hover:bg-surface-600 hover:text-slate-900 dark:hover:text-slate-300'
      )}
    >
      {children}
    </button>
  );
}

// ============================================
// TREND INDICATOR COMPONENT
// ============================================

function TrendIndicator({ value, suffix = '%' }) {
  if (value === 0 || value === null || value === undefined) {
    return (
      <span className="flex items-center text-slate-500 text-sm">
        <Minus className="w-4 h-4 mr-1" />
        No change
      </span>
    );
  }

  const isPositive = value > 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;

  return (
    <span className={clsx(
      'flex items-center text-sm font-medium',
      isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
    )}>
      <Icon className="w-4 h-4 mr-1" />
      {isPositive ? '+' : ''}{value}{suffix}
    </span>
  );
}

// ============================================
// HEATMAP COMPONENT
// ============================================

function HeatmapChart({ data, maxVisits, days, onCellClick, daysCount, referenceDate }) {
  // Use state to track dark mode so component re-renders when it changes
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  // Watch for theme changes
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    // Check immediately
    checkDarkMode();

    // Set up a MutationObserver to watch for class changes on the html element
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  if (!data || data.length === 0) {
    return <EmptyState title="No heatmap data" description="Visit data will appear once activity is recorded." icon={Clock} />;
  }

  const getColor = (value) => {
    if (value === 0) return isDark ? '#1e293b' : '#f1f5f9'; // surface-800 : surface-100
    const intensity = Math.min(value / Math.max(maxVisits, 1), 1);

    // Green gradient (same for both themes)
    // Low: #dcfce7 (220, 252, 231) -> High: #16a34a (22, 163, 74)
    const r = Math.round(220 + (22 - 220) * intensity);
    const g = Math.round(252 + (163 - 252) * intensity);
    const b = Math.round(231 + (74 - 231) * intensity);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12a';
    if (hour === 12) return '12p';
    return hour < 12 ? `${hour}a` : `${hour - 12}p`;
  };

  const dayDates = useMemo(() => {
    if (!daysCount || daysCount > 7 || !referenceDate) return {};

    const dates = {};
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Normalize reference date to noon to avoid timezone rollover issues
    const ref = new Date(referenceDate);
    ref.setHours(12, 0, 0, 0);

    // Look back up to daysCount days
    for (let i = 0; i < daysCount; i++) {
      const d = new Date(ref);
      d.setDate(d.getDate() - i);
      const dayName = dayNames[d.getDay()];

      // Only set if not already set (since we iterate backwards, we want the most recent occurrence)
      // Actually, we want the most recent 'Sun' to be labeled.
      // With <= 7 days, there's only one of each day.
      if (!dates[dayName]) {
        dates[dayName] = `${d.getMonth() + 1}/${d.getDate()}`;
      }
    }
    return dates;
  }, [daysCount, referenceDate]);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[400px]">
        <div className="flex">
          <div className="w-10" /> {/* Spacer for hour labels */}
          {days.map(day => (
            <div key={day} className="flex-1 text-center text-xs text-slate-500 pb-1 flex flex-col items-center">
              <span className="font-medium">{day}</span>
              {dayDates[day] && (
                <span className="text-[10px] text-slate-400 font-normal leading-none">{dayDates[day]}</span>
              )}
            </div>
          ))}
        </div>
        {data.map((row, hourIdx) => (
          <div key={hourIdx} className="flex items-center gap-0.5 mb-0.5">
            <div className="w-10 text-xs text-slate-500 text-right pr-2">
              {hourIdx % 3 === 0 ? formatHour(row.hour) : ''}
            </div>
            {days.map((day, dayIdx) => (
              <button
                key={day}
                className={clsx(
                  "flex-1 h-4 rounded-sm transition-all focus:outline-none focus:ring-2 focus:ring-brand-500",
                  row[day] > 0 ? "cursor-pointer hover:ring-2 hover:ring-white/50" : "cursor-default"
                )}
                style={{ backgroundColor: getColor(row[day]) }}
                title={`${day} ${formatHour(row.hour)}: ${row[day]} visits`}
                onClick={() => row[day] > 0 && onCellClick?.(row.hour, day, dayIdx)}
                disabled={row[day] === 0}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function HeatmapDrilldown({ hour, dayName, dayIdx, onClose, timezone, dateRange, filters }) {
  const { data, isLoading } = useQuery({
    queryKey: ['analytics', 'heatmap-detail', hour, dayIdx, timezone, dateRange, filters],
    queryFn: async () => {
      try {
        const queryParams = {
          hour,
          dayOfWeek: dayIdx,
          timezone,
          ...dateRange,
          ...filters
        };
        const response = await api.get(`/analytics/heatmap/detail?${new URLSearchParams(queryParams).toString()}`);
        return response;
      } catch (error) {
        console.error('Failed to fetch heatmap details:', error);
        return { topDomains: [] };
      }
    }
  });

  const formatHour = (h) => {
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-surface-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Usage Details</h3>
            <p className="text-sm text-slate-500">{dayName}s at {formatHour(hour)}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse flex items-center justify-between">
                  <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
                  <div className="h-4 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
              ))}
            </div>
          ) : !data?.topDomains?.length ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No specific domain details available for this time.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Top 5 Products / Domains</p>
              {data.topDomains.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between group">
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900 dark:text-white truncate max-w-[240px]">
                      {item.name}
                    </span>
                    <span className="text-xs text-slate-500 truncate max-w-[240px]">
                      {item.domain}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-brand-500">{item.visits.toLocaleString()}</div>
                    <div className="text-[10px] text-slate-400 uppercase">Visits</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-surface-50 dark:bg-surface-700/30 text-center">
          <p className="text-[10px] text-slate-400">Based on data from the last {dateRange.days} days in {timezone} timezone.</p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// EXPORT UTILITY
// ============================================

function exportToCSV(data, filename) {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(','),
    ...data.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(','))
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function Overview() {
  // Theme state for forcing heatmap re-render
  const { theme } = useTheme();

  // Date range state
  const [dateRangePreset, setDateRangePreset] = useState('7');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Filter states
  const [visitMetric, setVisitMetric] = useState('devices');
  const [selectedCampus, setSelectedCampus] = useState('');
  const [selectedUserType, setSelectedUserType] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showApprovedOnly, setShowApprovedOnly] = useState(false);
  const [showUnapprovedOnly, setShowUnapprovedOnly] = useState(false);

  // Drill-down modal states
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState(null);
  const [selectedHeatmapCell, setSelectedHeatmapCell] = useState(null);

  // PDF export state
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const dashboardRef = useRef(null);

  // Layout rearrangement state
  const [isLayoutLocked, setIsLayoutLocked] = useState(true);
  const defaultWidgetOrder = ['user-type', 'category', 'products', 'campus', 'heatmap', 'uncategorized'];

  // Widget order state (persisted to localStorage)
  const [widgetOrder, setWidgetOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate saved order contains all widgets
        if (defaultWidgetOrder.every(w => parsed.includes(w))) {
          return parsed;
        }
      }
      return defaultWidgetOrder;
    } catch {
      return defaultWidgetOrder;
    }
  });

  const [draggedWidget, setDraggedWidget] = useState(null);
  const [dragOverWidget, setDragOverWidget] = useState(null);

  const handleDragStart = useCallback((widgetId) => {
    if (isLayoutLocked) return;
    setDraggedWidget(widgetId);
  }, [isLayoutLocked]);

  const handleDragOver = useCallback((e, targetId) => {
    if (isLayoutLocked || !draggedWidget || draggedWidget === targetId) return;
    e.preventDefault();
    setDragOverWidget(targetId);

    setWidgetOrder(prev => {
      const newOrder = [...prev];
      const draggedIdx = newOrder.indexOf(draggedWidget);
      const targetIdx = newOrder.indexOf(targetId);

      if (draggedIdx === -1 || targetIdx === -1) return prev;

      newOrder.splice(draggedIdx, 1);
      newOrder.splice(targetIdx, 0, draggedWidget);

      return newOrder;
    });
  }, [isLayoutLocked, draggedWidget]);

  const handleDragEnd = useCallback(() => {
    if (draggedWidget) {
      try {
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(widgetOrder));
      } catch (e) {
        console.warn('Failed to save layout:', e);
      }
    }
    setDraggedWidget(null);
    setDragOverWidget(null);
  }, [draggedWidget, widgetOrder]);

  const resetLayout = useCallback(() => {
    setWidgetOrder(defaultWidgetOrder);
    try {
      localStorage.removeItem(LAYOUT_STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear layout:', e);
    }
  }, []);


  // Utility functions
  const toISODate = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const addDays = (isoDate, deltaDays) => {
    const d = new Date(`${isoDate}T00:00:00`);
    d.setDate(d.getDate() + deltaDays);
    return toISODate(d);
  };

  const safePreset = ['7', '30', '90', 'custom'].includes(dateRangePreset) ? dateRangePreset : '7';
  const effectiveDays = safePreset === 'custom' ? null : safePreset;

  // Build query string for API calls
  const buildQueryString = (baseParams = {}) => {
    const params = new URLSearchParams();
    Object.entries(baseParams).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).length > 0) params.set(k, String(v));
    });

    params.set('visitMetric', visitMetric);

    if (safePreset === 'custom') {
      if (customStartDate && customEndDate) {
        params.set('startDate', customStartDate);
        params.set('endDate', customEndDate);
      } else {
        params.set('days', '7');
      }
    } else {
      params.set('days', effectiveDays);
    }

    if (selectedCampus) params.set('campusId', selectedCampus);
    if (selectedUserType) params.set('userType', selectedUserType);
    if (selectedOrg) params.set('orgId', selectedOrg);

    return `?${params.toString()}`;
  };

  // PDF Export handler
  const handleExportPDF = async () => {
    setIsExportingPDF(true);
    try {
      await exportToPDF('dashboard-content', 'edframe-dashboard');
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setIsExportingPDF(false);
    }
  };

  // Drill-down helper - get current filters for modals
  const getDrilldownFilters = () => ({
    campusId: selectedCampus,
    userType: selectedUserType,
    visitMetric
  });

  const getDrilldownDateRange = () => ({
    days: safePreset === 'custom' ? 30 : parseInt(safePreset)
  });

  // ============================================
  // DATA QUERIES
  // ============================================

  // Organizations
  const { data: orgsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      try { return await api.get('/organizations'); }
      catch { return { organizations: [] }; }
    },
  });
  const organizations = orgsData?.organizations || [];

  // Default selection for single-org users
  useEffect(() => {
    if (organizations.length === 1 && !selectedOrg) {
      setSelectedOrg(organizations[0].id);
    }
  }, [organizations, selectedOrg]);

  // Campuses
  const { data: campusesData } = useQuery({
    queryKey: ['campuses', selectedOrg],
    queryFn: async () => {
      try { return await api.get(`/campuses?orgId=${selectedOrg}`); }
      catch { return { campuses: [] }; }
    },
    enabled: !!selectedOrg,
  });
  const campuses = campusesData?.campuses || [];

  // Overview with comparison
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['analytics', 'overview-comparison', safePreset, customStartDate, customEndDate, visitMetric, selectedCampus, selectedUserType],
    queryFn: async () => {
      try { return await api.get(`/analytics/overview-with-comparison${buildQueryString()}`); }
      catch { return null; }
    },
  });

  // Basic overview (for total counts)
  const { data: basicOverview, isLoading: basicOverviewLoading } = useQuery({
    queryKey: ['analytics', 'overview', safePreset, customStartDate, customEndDate, visitMetric, selectedCampus, selectedUserType],
    queryFn: async () => {
      try { return await api.get(`/analytics/overview${buildQueryString()}`); }
      catch { return null; }
    },
  });

  // Staff vs Student trend
  const { data: userTypeTrend, isLoading: userTypeTrendLoading } = useQuery({
    queryKey: ['analytics', 'trend-by-user-type', safePreset, customStartDate, customEndDate, selectedCampus, visitMetric],
    queryFn: async () => {
      try { return await api.get(`/analytics/trend-by-user-type${buildQueryString()}`); }
      catch { return { trend: [] }; }
    },
  });

  // Category breakdown
  const { data: categoryData, isLoading: categoryLoading } = useQuery({
    queryKey: ['analytics', 'categories', safePreset, customStartDate, customEndDate, visitMetric, selectedCampus, selectedUserType],
    queryFn: async () => {
      try { return await api.get(`/analytics/categories${buildQueryString()}`); }
      catch { return { categories: [] }; }
    },
  });

  // Campus comparison
  const { data: campusComparison, isLoading: campusComparisonLoading } = useQuery({
    queryKey: ['analytics', 'campus-comparison', safePreset, customStartDate, customEndDate, selectedOrg, selectedUserType, visitMetric],
    queryFn: async () => {
      try { return await api.get(`/analytics/campus-comparison${buildQueryString()}`); }
      catch { return { campuses: [] }; }
    },
    enabled: !!selectedOrg,
  });

  // Get timezone from selected organization, or first org, or use UTC as default
  const orgTimezone = useMemo(() => {
    const org = selectedOrg
      ? organizations.find(o => o.id === selectedOrg)
      : organizations[0];
    return org?.timezone || 'UTC';
  }, [organizations, selectedOrg]);

  // Heatmap
  const { data: heatmapData, isLoading: heatmapLoading } = useQuery({
    queryKey: ['analytics', 'heatmap', safePreset, customStartDate, customEndDate, selectedCampus, selectedUserType, orgTimezone],
    queryFn: async () => {
      try { return await api.get(`/analytics/heatmap${buildQueryString({ days: Math.max(parseInt(effectiveDays) || 30, 30), timezone: orgTimezone })}`); }
      catch { return { heatmap: [], maxVisits: 0, days: [] }; }
    },
  });

  // Top tools
  const { data: topToolsData, isLoading: topToolsLoading } = useQuery({
    queryKey: ['analytics', 'topTools', safePreset, customStartDate, customEndDate, visitMetric, selectedCampus, selectedUserType],
    queryFn: async () => {
      try { return await api.get(`/analytics/tools${buildQueryString({ limit: 8 })}`); }
      catch { return { tools: [] }; }
    },
  });

  // Uncategorized domains
  const { data: uncategorizedData, isLoading: uncategorizedLoading } = useQuery({
    queryKey: ['analytics', 'uncategorized', safePreset, selectedCampus, selectedUserType],
    queryFn: async () => {
      try { return await api.get(`/analytics/uncategorized${buildQueryString({ limit: 5 })}`); }
      catch { return { domains: [] }; }
    },
  });

  // Categories list (for filter)
  const { data: allCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      try { return await api.get('/tools/categories/list'); }
      catch { return { categories: [] }; }
    },
  });

  // ============================================
  // COMPUTED DATA
  // ============================================

  const stats = basicOverview || {};
  const changes = overviewData?.changes || {};

  // Filter top tools by approval status and category
  const filteredTools = useMemo(() => {
    let tools = topToolsData?.tools || [];
    if (showApprovedOnly) tools = tools.filter(t => t.is_approved);
    if (showUnapprovedOnly) tools = tools.filter(t => !t.is_approved);
    if (selectedCategory) tools = tools.filter(t => t.category === selectedCategory);
    return tools;
  }, [topToolsData, showApprovedOnly, showUnapprovedOnly, selectedCategory]);

  // Format trend data for charts
  const userTypeTrendFormatted = useMemo(() => {
    return (userTypeTrend?.trend || []).map(d => ({
      ...d,
      dateLabel: new Date(d.date).toLocaleDateString('en-US', { weekday: 'short' })
    }));
  }, [userTypeTrend]);

  // Pie chart colors
  const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#64748b'];

  // Calculate date context for heatmap headers
  const dateContext = useMemo(() => {
    // If not custom, reference is today
    if (safePreset !== 'custom') {
      return {
        referenceDate: new Date(),
        daysCount: parseInt(safePreset)
      };
    }

    // Custom range
    if (customStartDate && customEndDate) {
      // Parse YYYY-MM-DD strings as local dates (use noon to be safe)
      const parseDate = (s) => {
        const [y, m, d] = s.split('-').map(Number);
        return new Date(y, m - 1, d, 12, 0, 0);
      };

      const start = parseDate(customStartDate);
      const end = parseDate(customEndDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // inclusive

      return { referenceDate: end, daysCount: diffDays };
    }

    return { referenceDate: new Date(), daysCount: 7 };
  }, [safePreset, customStartDate, customEndDate]);

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      <div id="dashboard-content" className="space-y-6">
        {/* Header with Filters */}
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold mb-1 text-slate-900 dark:text-white">Overview</h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Monitor product usage across your district</p>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center gap-2">
              {/* Layout Controls */}
              <button
                onClick={() => setIsLayoutLocked(!isLayoutLocked)}
                className={clsx(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                  isLayoutLocked
                    ? 'bg-surface-200 dark:bg-surface-700 text-slate-500 dark:text-slate-400 hover:bg-surface-300 dark:hover:bg-surface-600'
                    : 'bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/50'
                )}
              >
                {isLayoutLocked ? (
                  <>
                    <Lock className="w-4 h-4" />
                    <span className="hidden sm:inline">Unlock</span>
                  </>
                ) : (
                  <>
                    <Unlock className="w-4 h-4" />
                    <span className="hidden sm:inline">Lock</span>
                  </>
                )}
              </button>

              {!isLayoutLocked && (
                <button
                  onClick={resetLayout}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-surface-200 dark:bg-surface-700 text-slate-500 dark:text-slate-400 hover:bg-surface-300 dark:hover:bg-surface-600 transition-colors"
                  title="Reset to default layout"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}

              <div className="w-px h-6 bg-surface-200 dark:bg-surface-700 mx-1" />

              {/* Export Buttons */}
              <button
                onClick={() => exportToCSV(filteredTools, 'edframe-products')}
                className="btn btn-secondary flex items-center gap-2"
                disabled={!filteredTools.length}
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">CSV</span>
              </button>
              <button
                onClick={handleExportPDF}
                className="btn btn-secondary flex items-center gap-2"
                disabled={isExportingPDF}
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">{isExportingPDF ? '...' : 'PDF'}</span>
              </button>
            </div>
          </div>

          {/* Quick Filter Pills */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Range Pills */}
            <div className="flex items-center gap-1 bg-surface-100 dark:bg-surface-800 rounded-full p-1 border border-surface-200 dark:border-surface-700">
              {['7', '30', '90'].map(d => (
                <FilterPill
                  key={d}
                  active={safePreset === d}
                  onClick={() => setDateRangePreset(d)}
                >
                  {d}d
                </FilterPill>
              ))}
              <FilterPill
                active={safePreset === 'custom'}
                onClick={() => {
                  setDateRangePreset('custom');
                  const today = toISODate(new Date());
                  setCustomEndDate(today);
                  setCustomStartDate(addDays(today, -7));
                }}
              >
                Custom
              </FilterPill>
            </div>

            {/* User Type Pills */}
            <div className="flex items-center gap-1 bg-surface-100 dark:bg-surface-800 rounded-full p-1 border border-surface-200 dark:border-surface-700">
              <FilterPill active={!selectedUserType} onClick={() => setSelectedUserType('')}>
                All
              </FilterPill>
              <FilterPill active={selectedUserType === 'staff'} onClick={() => setSelectedUserType('staff')}>
                Staff
              </FilterPill>
              <FilterPill active={selectedUserType === 'student'} onClick={() => setSelectedUserType('student')}>
                Students
              </FilterPill>
            </div>

            {/* Approval Status Pills */}
            <div className="flex items-center gap-1 bg-surface-100 dark:bg-surface-800 rounded-full p-1 border border-surface-200 dark:border-surface-700">
              <FilterPill
                active={!showApprovedOnly && !showUnapprovedOnly}
                onClick={() => { setShowApprovedOnly(false); setShowUnapprovedOnly(false); }}
              >
                All
              </FilterPill>
              <FilterPill
                active={showApprovedOnly}
                onClick={() => { setShowApprovedOnly(true); setShowUnapprovedOnly(false); }}
              >
                Approved
              </FilterPill>
              <FilterPill
                active={showUnapprovedOnly}
                onClick={() => { setShowApprovedOnly(false); setShowUnapprovedOnly(true); }}
              >
                Unapproved
              </FilterPill>
            </div>

            {/* Count Type Pills */}
            <div className="flex items-center gap-1 bg-surface-100 dark:bg-surface-800 rounded-full p-1 border border-surface-200 dark:border-surface-700">
              <FilterPill active={visitMetric === 'events'} onClick={() => setVisitMetric('events')}>
                Per Visit
              </FilterPill>
              <FilterPill active={visitMetric === 'unique_device_days'} onClick={() => setVisitMetric('unique_device_days')}>
                Per Device
              </FilterPill>
            </div>
          </div>

          {/* Advanced Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Custom Date Inputs */}
            {safePreset === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="input text-sm"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
                <span className="text-slate-500 text-sm">to</span>
                <input
                  type="date"
                  className="input text-sm"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            )}

            {/* Organization Filter */}
            {organizations.length > 0 && (
              <select
                className="input text-sm w-40"
                value={selectedOrg}
                onChange={(e) => {
                  setSelectedOrg(e.target.value);
                  setSelectedCampus('');
                }}
              >
                <option value="">All Organizations</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            )}

            {/* Campus Filter */}
            {selectedOrg && campuses.length > 0 && (
              <select
                className="input text-sm w-40"
                value={selectedCampus}
                onChange={(e) => setSelectedCampus(e.target.value)}
              >
                <option value="">All Campuses</option>
                {campuses.map(campus => (
                  <option key={campus.id} value={campus.id}>{campus.name}</option>
                ))}
              </select>
            )}

            {/* Category Filter */}
            {allCategories?.categories?.length > 0 && (
              <select
                className="input text-sm w-40"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {allCategories.categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Stats Cards with Growth Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(overviewLoading || basicOverviewLoading) ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              <div className="card p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-500 dark:text-slate-400 text-sm">Active Devices</span>
                  <Monitor className="w-5 h-5 text-brand-400" />
                </div>
                <div className="text-3xl font-bold mb-1">
                  {stats.totalUsers?.toLocaleString() || '0'}
                </div>
                <div className="text-xs text-slate-500">
                  {stats.activeDevices?.toLocaleString() || '0'} active in period
                </div>
              </div>

              <div className="card p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-500 dark:text-slate-400 text-sm">Total Visits</span>
                  <Activity className="w-5 h-5 text-brand-400" />
                </div>
                <div className="text-3xl font-bold mb-1">
                  {stats.totalVisits?.toLocaleString() || '0'}
                </div>
                <TrendIndicator value={changes.visits} />
              </div>

              <div className="card p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-500 dark:text-slate-400 text-sm">Products Used</span>
                  <Wrench className="w-5 h-5 text-brand-400" />
                </div>
                <div className="text-3xl font-bold mb-1">
                  {stats.activeTools?.toString() || '0'}
                </div>
                <TrendIndicator value={changes.tools} />
              </div>

              <div className="card p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-500 dark:text-slate-400 text-sm">Unapproved Products</span>
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div className="text-3xl font-bold mb-1 text-amber-400">
                  {stats.shadowIT?.toString() || '0'}
                </div>
                <div className="text-xs text-slate-500">
                  {stats.pendingReview || 0} domains pending review
                </div>
              </div>
            </>
          )}
        </div>

        {/* Draggable Charts Grid */}
        {!isLayoutLocked && (
          <div className="bg-brand-500/10 border border-brand-500/30 rounded-lg px-4 py-2 mb-4 text-sm text-brand-400 flex items-center gap-2">
            <GripVertical className="w-4 h-4" />
            Drag widgets to rearrange. Changes are saved automatically.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {widgetOrder.map((widgetId) => {
            const commonProps = {
              key: widgetId,
              id: widgetId,
              isLocked: isLayoutLocked,
              isDragging: draggedWidget === widgetId,
              onDragStart: handleDragStart,
              onDragOver: handleDragOver,
              onDragEnd: handleDragEnd,
            };

            // User Type Chart
            if (widgetId === 'user-type') {
              if (userTypeTrendLoading) return <SkeletonChart key={widgetId} />;
              return (
                <DraggableWidget {...commonProps} title="Staff vs Student Usage">
                  {userTypeTrendFormatted.length === 0 ? (
                    <EmptyState
                      title="No usage data"
                      description="Deploy campus tokens to see staff vs student breakdown."
                      icon={Activity}
                    />
                  ) : (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={userTypeTrendFormatted}>
                          <defs>
                            <linearGradient id="colorStaff" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorStudent" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                          <XAxis dataKey="dateLabel" stroke="#64748b" fontSize={12} />
                          <YAxis stroke="#64748b" fontSize={12} />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload) return null;
                              return (
                                <div className="bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg p-3 shadow-lg">
                                  <p className="font-medium text-slate-900 dark:text-white mb-2">{label}</p>
                                  {payload.map((entry, idx) => (
                                    <p key={idx} className="text-sm" style={{ color: entry.color }}>
                                      {entry.name}: {entry.value.toLocaleString()}
                                    </p>
                                  ))}
                                </div>
                              );
                            }}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="staffVisits" name="Staff" stroke="#22c55e" fill="url(#colorStaff)" strokeWidth={2} />
                          <Area type="monotone" dataKey="studentVisits" name="Students" stroke="#3b82f6" fill="url(#colorStudent)" strokeWidth={2} />
                          <Line type="monotone" dataKey="unknownVisits" name="Unassigned" stroke="#64748b" strokeDasharray="5 5" dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </DraggableWidget>
              );
            }

            // Category Chart
            if (widgetId === 'category') {
              if (categoryLoading) return <SkeletonChart key={widgetId} />;
              return (
                <DraggableWidget {...commonProps} title="Usage by Category">
                  {!categoryData?.categories?.length ? (
                    <EmptyState
                      title="No category data"
                      description="Categorize products to see usage breakdown."
                      icon={Wrench}
                    />
                  ) : (
                    <>
                      <div className="h-64 flex">
                        <ResponsiveContainer width="60%" height="100%">
                          <PieChart>
                            <Pie
                              data={categoryData.categories}
                              dataKey="visits"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={2}
                              onClick={(data) => {
                                if (data?.name) {
                                  setSelectedCategoryName(data.name);
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {categoryData.categories.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.[0]) return null;
                                const isDark = document.documentElement.classList.contains('dark');
                                return (
                                  <div className="bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg p-3 shadow-lg">
                                    <p className="font-medium text-slate-900 dark:text-white">{payload[0].name}</p>
                                    <p className="text-sm text-slate-600 dark:text-slate-400">{payload[0].value.toLocaleString()} visits</p>
                                  </div>
                                );
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex-1 flex flex-col justify-center gap-2 overflow-y-auto max-h-64">
                          {categoryData.categories.slice(0, 6).map((cat, idx) => (
                            <button
                              key={cat.name}
                              className="flex items-center gap-2 text-sm hover:bg-surface-100 dark:hover:bg-surface-700 rounded px-2 py-1 transition-colors text-left"
                              onClick={() => setSelectedCategoryName(cat.name)}
                            >
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: cat.color || COLORS[idx % COLORS.length] }}
                              />
                              <span className="text-slate-700 dark:text-slate-300 truncate">{cat.name}</span>
                              <span className="text-slate-500 ml-auto">{cat.visits.toLocaleString()}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 text-center mt-2">Click a category to see products</p>
                    </>
                  )}
                </DraggableWidget>
              );
            }

            // Products Chart
            if (widgetId === 'products') {
              if (topToolsLoading) return <SkeletonChart key={widgetId} />;
              return (
                <DraggableWidget {...commonProps} title="Top Products by Usage">
                  {filteredTools.length === 0 ? (
                    <EmptyState
                      title="No products found"
                      description="Adjust your filters or wait for usage data."
                      icon={Wrench}
                    />
                  ) : (
                    <>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={filteredTools}
                            layout="vertical"
                            onClick={(data) => {
                              if (data?.activePayload?.[0]?.payload?.id) {
                                setSelectedProductId(data.activePayload[0].payload.id);
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                            <XAxis type="number" stroke="#64748b" fontSize={12} />
                            <YAxis
                              dataKey="name"
                              type="category"
                              stroke="#64748b"
                              fontSize={11}
                              width={100}
                              tick={{ fill: '#94a3b8' }}
                            />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.[0]) return null;
                                return (
                                  <div className="bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg p-3 shadow-lg">
                                    <p className="font-medium text-slate-900 dark:text-white">{payload[0].payload.name}</p>
                                    <p className="text-sm text-slate-600 dark:text-slate-400">
                                      {payload[0].value.toLocaleString()} {visitMetric === 'unique_device_days' ? 'Device' : 'Visits'}
                                    </p>
                                  </div>
                                );
                              }}
                            />
                            <Bar dataKey="visits" fill="#22c55e" radius={[0, 4, 4, 0]}>
                              {filteredTools.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={entry.is_approved ? '#22c55e' : '#f59e0b'}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <p className="text-xs text-slate-500 text-center mt-2">Click a product for detailed view</p>
                    </>
                  )}
                </DraggableWidget>
              );
            }

            // Campus/Heatmap Chart
            if (widgetId === 'campus') {
              if (selectedOrg || organizations.length > 1) {
                if (campusComparisonLoading) return <SkeletonChart key={widgetId} />;
                return (
                  <DraggableWidget {...commonProps} title={selectedOrg ? "Campus Comparison" : "Organization Comparison"}>
                    {!campusComparison?.campuses?.length ? (
                      <EmptyState
                        title="No comparison data"
                        description="Add data to see comparison."
                        icon={Monitor}
                      />
                    ) : (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={campusComparison.campuses}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                            <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
                            <YAxis stroke="#64748b" fontSize={12} />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.[0]) return null;
                                return (
                                  <div className="bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg p-3 shadow-lg">
                                    <p className="font-medium text-slate-900 dark:text-white mb-1">{payload[0].payload.name}</p>
                                    {payload.map((entry, idx) => (
                                      <p key={idx} className="text-sm text-slate-600 dark:text-slate-400">
                                        {entry.name}: {entry.value.toLocaleString()}
                                      </p>
                                    ))}
                                  </div>
                                );
                              }}
                            />
                            <Legend />
                            <Bar dataKey="visits" name="Visits" fill="#22c55e" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="devices" name="Devices" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </DraggableWidget>
                );
              }
              return null;
            }

            // Heatmap Chart
            if (widgetId === 'heatmap') {
              if (heatmapLoading) return <SkeletonChart key={widgetId} />;
              return (
                <DraggableWidget {...commonProps} title="Peak Usage Times">
                  <HeatmapChart
                    data={heatmapData?.heatmap}
                    maxVisits={heatmapData?.maxVisits}
                    days={heatmapData?.days || []}
                    onCellClick={(hour, day, dayIdx) => setSelectedHeatmapCell({ hour, day, dayIdx })}
                    daysCount={dateContext.daysCount}
                    referenceDate={dateContext.referenceDate}
                  />
                </DraggableWidget>
              );
            }

            // Uncategorized Domains Widget
            if (widgetId === 'uncategorized') {
              return (
                <DraggableWidget {...commonProps}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Uncategorized Domains</h3>
                    <a href="/tools" className="text-sm text-brand-400 hover:text-brand-300 flex items-center gap-1">
                      Review all <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  {uncategorizedLoading ? (
                    <div className="animate-pulse space-y-3">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-12 bg-surface-100 dark:bg-surface-700 rounded" />
                      ))}
                    </div>
                  ) : !uncategorizedData?.domains?.length ? (
                    <EmptyState
                      title="All domains categorized"
                      description="Great job! All visited domains have been categorized."
                      icon={Wrench}
                    />
                  ) : (
                    <div className="space-y-2">
                      {uncategorizedData.domains.map((domain, idx) => (
                        <div
                          key={domain.domain}
                          className="flex items-center justify-between p-3 bg-surface-100 dark:bg-surface-800 rounded-lg hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors border border-surface-200 dark:border-surface-700"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-slate-500 text-sm w-6">{idx + 1}.</span>
                            <span className="font-mono text-sm text-slate-900 dark:text-white">{domain.domain}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-slate-600 dark:text-slate-400">
                              {domain.visits.toLocaleString()} visits
                            </span>
                            <span className="text-slate-500">
                              {domain.devices} devices
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </DraggableWidget>
              );
            }

            return null;
          })}
        </div>
      </div>

      {/* Drill-down Modals */}
      {selectedProductId && (
        <ProductDrilldown
          productId={selectedProductId}
          onClose={() => setSelectedProductId(null)}
          dateRange={getDrilldownDateRange()}
          filters={getDrilldownFilters()}
        />
      )}

      {selectedCategoryName && (
        <CategoryDrilldown
          categoryName={selectedCategoryName}
          onClose={() => setSelectedCategoryName(null)}
          onProductClick={(productId) => setSelectedProductId(productId)}
          dateRange={getDrilldownDateRange()}
          filters={getDrilldownFilters()}
        />
      )}

      {selectedHeatmapCell && (
        <HeatmapDrilldown
          hour={selectedHeatmapCell.hour}
          dayName={selectedHeatmapCell.day}
          dayIdx={selectedHeatmapCell.dayIdx}
          timezone={orgTimezone}
          dateRange={getDrilldownDateRange()}
          filters={getDrilldownFilters()}
          onClose={() => setSelectedHeatmapCell(null)}
        />
      )}
    </>
  );
}
